import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

export class MediaGallerySimple extends Component {
  constructor() {
    super();
    this.controller = new AbortController();
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.controller;
    const target = this.closest('.shopify-section, dialog');

    if (target) {
      target.addEventListener(
        ThemeEvents.variantUpdate,
        this.handleVariantUpdate,
        { signal }
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller.abort();
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  handleVariantUpdate(event) {
    const source = event.detail?.data?.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery-simple');
    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  }
}

if (!customElements.get('media-gallery-simple')) {
  customElements.define('media-gallery-simple', MediaGallerySimple);
}

if (!customElements.get('media-gallery-simple')) {
  customElements.define('media-gallery-simple', MediaGallerySimple);
}




class ProductMediaGalleryNew extends HTMLElement {
  constructor() {
    super();
    this.main = null;
    this.thumbs = null;
  }

  connectedCallback() {
    this.initSplide();
  }

  disconnectedCallback() {
    this.destroySplide();
  }

  initSplide() {
    const mainEl = this.querySelector('#main-slider');
    const thumbsEl = this.querySelector('#thumbnail-slider');

    if (!mainEl || !thumbsEl) return;

    this.main = new Splide(mainEl, {
      type: 'fade',
      rewind: true,
      pagination: false,
      arrows: true,
    });

    this.thumbs = new Splide(thumbsEl, {
      autoWidth: true,
      gap: 15,
      rewind: true,
      pagination: false,
      arrows: false,
      isNavigation: true,
      focus: !!this.dataset.thumbCentered ? 'center' : 1,
      breakpoints: {
        750: {
          gap: 10,
        },
      },
    });

    this.main.sync(this.thumbs);
    this.main.mount();
    this.thumbs.mount();
  }

  destroySplide() {
    if (this.main) {
      this.main.destroy(true);
      this.main = null;
    }

    if (this.thumbs) {
      this.thumbs.destroy(true);
      this.thumbs = null;
    }
  }
}

customElements.define('product-media-gallery-new', ProductMediaGalleryNew);
